import React, {useState, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {type RootState, type AppDispatch} from 'redux/store'
import {useNavigate} from 'react-router-dom'
import {Box, Grid, Typography} from '@mui/material'
import {LoadingButton} from '@mui/lab'
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import {useFormik} from 'formik'
import * as yup from 'yup'
import FormikSelect from 'shared/FormikSelect'
import FormikTextField from 'shared/FormikTextField'
import {PAGE_NAME} from 'config/constant'
import {fetchCarrierList} from 'redux/common.slice'
import {getShipmentDetails} from 'features/HomePage/TrackShipments/trackshipment.action'
import 'features/HomePage/TrackShipments/index.scss'
import {ENDPOINT} from 'config/path'
import {toggleLoginPopup, navigatePath, viewScheduleValues} from 'features/Login/login.slice'
import _ from 'lodash'

interface TrackShipmentProps {
    inPage?: boolean
}

const TrackShipmentForm: React.FC<TrackShipmentProps> = ({inPage = false}) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const theme = useTheme()
    const aboveMd = useMediaQuery(theme.breakpoints.up('md'))
    const aboveLg = useMediaQuery(theme.breakpoints.up('lg'))

    const [carrierOptions, setCarrierOptions] = useState([])
    const [isDisabled, setDisabled] = useState(true)

    const carrierList = useSelector((state: RootState) => state.common.carrier)
    const isLoggedIn = useSelector((state: any) => state?.login?.isLoggedIn)
    const {isLoading, initialValues: defaultValues} = useSelector((state: RootState) => state.trackShipment)
    const securityParams = useSelector((state: RootState) => state.login.loginDetails?.securityParams)
    // Inital Load
    useEffect(() => {
        // Fetch the Initial Carrier List
        void dispatch(
            fetchCarrierList({
                pageName: PAGE_NAME[29]
            })
        )
    }, [dispatch])

    // Update the carriers state with the fetched carrier list
    useEffect(() => {
        const list = carrierList
            ?.filter(({name}) => name !== 'AA')
            ?.sort(({name: firstName}, {name: secondName}) => {
                if (firstName > secondName) return 1
                else return -1
            })
            ?.map(({code, displayName, name}) => ({
                value: `${code} - ${displayName} (${name})`,
                label: `${displayName} (${name})`
            }))
        setCarrierOptions(list)
    }, [carrierList])

    // Handle the form submission
    const handleFormSubmit = async (formData: Record<string, string>) => {
        if (isLoggedIn) {
            await dispatch(getShipmentDetails({...formData, userId: _.get(securityParams, 'userID', 'CPSGUEST')}))
                .then(() => {
                    navigate(ENDPOINT.trackShipment)
                })
                .catch(() => {})
        } else {
            dispatch(toggleLoginPopup(true))
            dispatch(navigatePath(ENDPOINT.trackShipment))
            dispatch(viewScheduleValues(formData))
        }
    }

    // Formik Initialization
    const formik = useFormik({
        initialValues: {...defaultValues},
        validationSchema: yup.object({
            carrier: yup.string().notOneOf(['-Select-'], 'Carrier is required.').required('Carrier is required.'),
            airlineCode: yup
                .string()
                .required('Airline Code is required.')
                .min(3, 'Airline Code is invalid.')
                .max(3, 'Airline Code is invalid.'),
            airWayBill: yup
                .string()
                .required('Air Waybill is required.')
                .min(8, 'Air Waybill No. is invalid.')
                .max(9, 'Air Waybill No. is invalid.')
        }),
        onSubmit: async (values) => await handleFormSubmit(values)
    })

    // Handle the changes in the form fields and submit validation
    useEffect(() => {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const {carrier, airlineCode, airWayBill} = formik?.values
        // const isAlphabetRegex = /[^0-9]/g
        const isSpecialCharRegex = /[!@©#$%^&*\\(),.?'":;{}|<>[\]_+\-/=`~ ]/g

        // Handle submit button validation
        if (carrier !== '-Select-' && airlineCode !== '' && airWayBill !== '' && formik.isValid) setDisabled(false)
        else setDisabled(true)

        void formik.setFieldValue('airlineCode', airlineCode.replace(isSpecialCharRegex, ''))
        void formik.setFieldValue('airWayBill', airWayBill.replace(isSpecialCharRegex, ''))
    }, [formik?.values, formik.isValid])

    const handleInputChange = (event: any) => {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const {name, value} = event?.target

        if (name === 'carrier') {
            if (value.includes('-Select-') || value.includes('AF-KL')) {
                void formik.setFieldValue('airlineCode', '')
                void formik.setTouched({}, false)
            } else void formik.setFieldValue('airlineCode', value.slice(0, 3))
        }
    }

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Grid container className={`form-grid-container ${!inPage && 'form-padding'}`} spacing={2} rowGap={1}>
                    {/* CARRIER */}
                    <Grid item xs={12} sm={3} md={3} className={`form-field ${aboveMd && 'maxWidthField'}`}>
                        <Typography className='form-control__label' component='label' htmlFor='carrier'>
                            Carrier<span>*</span>
                        </Typography>
                        <FormikSelect
                            id='carrier'
                            formik={formik}
                            options={carrierOptions}
                            onChange={handleInputChange}
                            className='formik-input__select'
                            placeholder='- Select Carrier -'
                            disabled={isLoading}
                            autoFocus={!inPage}
                            fullWidth
                        />
                    </Grid>
                    {/* AIRLINE CODE */}
                    <Grid item xs={12} sm={3} md={3} className={`form-field ${aboveMd && 'maxWidthField'}`}>
                        <Typography className='form-control__label' component='label' htmlFor='airlineCode'>
                            Airline Code<span>*</span>
                        </Typography>
                        <FormikTextField
                            id='airlineCode'
                            formik={formik}
                            maxLength={3}
                            disabled={isLoading}
                            className='formik-input__textfield'
                            fullWidth
                        />
                    </Grid>
                    {/* AIRWAY BILL */}
                    <Grid item xs={12} sm={3} md={3} className={`form-field ${aboveMd && 'maxWidthField'}`}>
                        <Typography className='form-control__label' component='label' htmlFor='airWayBill'>
                            Air Waybill<span>*</span>
                        </Typography>
                        <FormikTextField
                            id='airWayBill'
                            formik={formik}
                            maxLength={9}
                            disabled={isLoading}
                            className='formik-input__textfield'
                            fullWidth
                        />
                    </Grid>
                    {/* SUBMIT BUTTON */}
                    <Grid item xs={12} sm={3} md={3} lg={2} className={`form-btn ${aboveLg && 'maxWidthBtn'}`}>
                        <LoadingButton
                            type='submit'
                            variant='contained'
                            className='formik-input__submitBtn outlinedInherit'
                            loading={isLoading}
                            disabled={isDisabled}
                            fullWidth
                        >
                            {!isLoading && 'Track shipments'}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>
        </Box>
    )
}

export default TrackShipmentForm
